import { ManuscriptClient } from "@/lib/clients";
import { Novel } from "@/lib/models";
import { ContestEntryItemModel, NolaContest } from "@/lib/models/nolaContest";
import { mapWithConcurrency } from "@/lib/utils/mapWithConcurrency";

/** 原稿本文の同時取得数。作品数 × 原稿数の一斉取得を避ける。 */
const MANUSCRIPT_CONTENT_CONCURRENCY = 4;

export type ManuscriptCharacterRequirement = {
  min?: number;
  max?: number;
};

export function getNovelCoverImageSrc(novel: Novel): string {
  const { novelId, image } = novel;
  if (image && image.startsWith("file:")) {
    return `file:novels/${novelId}/cover.jpg`;
  }
  return image ?? "";
}

export function getMergedContestEntryItems(contest: NolaContest, categoryId: string): ContestEntryItemModel[] {
  const baseItems = contest.entryItems ?? [];
  const categoryItems =
    (categoryId && contest.categories?.find((category) => category.id === categoryId)?.entryItems) || [];
  return [...baseItems, ...categoryItems].sort((a, b) => (a.order || 0) - (b.order || 0));
}

export function getManuscriptCharacterRequirement(
  contest: NolaContest,
  categoryId: string,
): ManuscriptCharacterRequirement {
  const manuscriptItems = getMergedContestEntryItems(contest, categoryId).filter(
    (item) => item.type === "manuscripts",
  );

  let min: number | undefined;
  let max: number | undefined;

  manuscriptItems.forEach((item) => {
    if (item.type !== "manuscripts") return;
    if (item.min !== undefined) {
      min = min === undefined ? item.min : Math.max(min, item.min);
    }
    if (item.max !== undefined) {
      max = max === undefined ? item.max : Math.min(max, item.max);
    }
  });

  return { min, max };
}

export function hasManuscriptCharacterRequirement(requirement: ManuscriptCharacterRequirement): boolean {
  return requirement.min !== undefined || requirement.max !== undefined;
}

export async function fetchNovelTotalCharacterCount(novelId: string): Promise<number> {
  const manuscriptClient = new ManuscriptClient();
  const manuscripts = await manuscriptClient.fetchAllManuscript(novelId);
  if (!manuscripts.length) return 0;

  const counts = await mapWithConcurrency(manuscripts, MANUSCRIPT_CONTENT_CONCURRENCY, async (manuscript) => {
    if (!manuscript.key) return 0;
    const content = await manuscriptClient.fetchManuscriptContent(novelId, manuscript.key);
    return content?.length ?? 0;
  });

  return counts.reduce((sum, count) => sum + count, 0);
}

export type CharacterCountIssueKey =
  | "insufficientCharacterCount"
  | "excessiveCharacterCount"
  | "characterCountLoadError";

const NOVEL_CHARACTER_COUNT_CONCURRENCY = 4;

export function buildCharacterCountIssueKeys(
  totalCharacterCount: number,
  requirement: ManuscriptCharacterRequirement,
): Exclude<CharacterCountIssueKey, "characterCountLoadError">[] {
  const issues: Exclude<CharacterCountIssueKey, "characterCountLoadError">[] = [];
  if (requirement.min !== undefined && totalCharacterCount < requirement.min) {
    issues.push("insufficientCharacterCount");
  }
  if (requirement.max !== undefined && totalCharacterCount > requirement.max) {
    issues.push("excessiveCharacterCount");
  }
  return issues;
}

/** 文字数条件が無い作品は本文を取得しない。取得失敗は作品単位で閉じる。 */
export async function resolveNovelCharacterCountIssues(
  novelIds: readonly string[],
  requirement: ManuscriptCharacterRequirement,
): Promise<Array<{ novelId: string; issueKeys: CharacterCountIssueKey[] }>> {
  if (!hasManuscriptCharacterRequirement(requirement)) {
    return novelIds.map((novelId) => ({ novelId, issueKeys: [] }));
  }

  return mapWithConcurrency(novelIds, NOVEL_CHARACTER_COUNT_CONCURRENCY, async (novelId) => {
    try {
      const totalCharacterCount = await fetchNovelTotalCharacterCount(novelId);
      return {
        novelId,
        issueKeys: buildCharacterCountIssueKeys(totalCharacterCount, requirement),
      };
    } catch {
      return { novelId, issueKeys: ["characterCountLoadError"] };
    }
  });
}
